import type { ChangeEvent } from 'react';
import React, { useMemo } from 'react';
import cx from 'classnames';
import T from 'i18n-react';

import { MultiSelect } from '@motorway/motorway-storybook-cra';

import { formatAsSelectOptions } from 'Components/VehicleList/VehicleList.helpers';
import {
	triggerMakeDropdownExpandedEvent,
	triggerMakeSearchInputEvent,
	triggerMakeSelectedEvent,
} from 'Services/analytics/events';
import {
	useFilters,
	useFiltersConfig,
	useSetFilter,
} from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { useMakes } from 'Stores/MakesStore/MakesStore';
import type { Filter } from 'Types/filter';
import type { Makes } from 'Types/make';
import { APP_THEME } from 'Utilities/consts';
import { useValueChangedCount } from 'Utilities/hooks/useValueChangedCount';

import { mapValuesToSelect, onMultiSelectChange, triggerEvent, useMultiSelectInputValues } from '../Filters.helpers';

import styles from './MakeMultiSelect.module.scss';

type OnMakeDropdownSelectText = { text: string };
type OnMakeDropdownSelectValue = { value: OnMakeDropdownSelectText[] };

export const MakeMultiSelect: React.FC = () => {
	const { make } = useFilters() as Filter;
	const filtersConfig = useFiltersConfig();
	const makes = useMakes() as Makes;
	const setFilter = useSetFilter();

	const { prevMakeSelectInputValue, setPrevMakeSelectInputValue } = useMultiSelectInputValues();

	const formattedMakes = useMemo(
		() =>
			formatAsSelectOptions({
				options: makes,
				text: 'name',
				textSuffix: 'total',
				value: 'slug',
			}),
		[makes],
	);

	const onMakeSelectChange = onMultiSelectChange({
		callback: triggerEvent({
			previousValue: prevMakeSelectInputValue,
			setPreviousValue: setPrevMakeSelectInputValue,
			trigger: triggerMakeSearchInputEvent,
		}),
		setValue: setFilter,
	});

	const onBlur = () => {
		setPrevMakeSelectInputValue('');
	};

	const onFocus = () => {
		triggerMakeDropdownExpandedEvent();
	};

	const onSelect = ({ text }: OnMakeDropdownSelectText, { value }: OnMakeDropdownSelectValue) => {
		const isSelected = value.some((v) => v.text === text);
		triggerMakeSelectedEvent({ isSelected, text });
	};

	const onChange = (e: ChangeEvent) => {
		onMakeSelectChange(e, filtersConfig.make.id);
	};

	const optionsChangedCount = useValueChangedCount(formattedMakes);

	return (
		<MultiSelect
			key={optionsChangedCount} // Re-mounting because MultiSelect is not able to change options once initialized
			searchBar
			className={cx('mw-input-select mw-input-multiselect mw-input-multiselect-dark', styles.selectBox, styles.make)}
			id="multiselect-make"
			inputProps={{
				onBlur,
				onFocus,
				onSelect,
			}}
			label={T.translate('vehicleList.filters.make')}
			// MultiSelect prop types do not have some props specified therefore TS is complaining
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			onChange={onChange}
			options={formattedMakes}
			placeholder={T.translate('vehicleList.filters.makePlaceholder')}
			theme={APP_THEME}
			value={formattedMakes.length ? mapValuesToSelect(make, formattedMakes) : []}
		/>
	);
};
